import { ComponentProps } from 'preact';
import { ReactNode } from 'preact/compat';

import { cn } from '@/lib/utils';
import { HabitatListItem } from '@/queries/habitats/types';

import { buttonVariants } from '../Buttons';
import HabitatStatus from '../HabitatCard/HabitatStatus';
import HabitatLink, { HabitatLinkProps } from '../HabitatLink';

interface HabitatGridItemProps extends ComponentProps<'div'> {
  habitat: HabitatListItem;
  renderLink?: (props: HabitatLinkProps) => ReactNode;
}

const HabitatGridItem = ({ habitat, className, renderLink, ...props }: HabitatGridItemProps) => {
  return (
    <div
      className={cn('flex flex-col rounded-2xl bg-white shadow-[0px_4px_12px_0px_rgba(0,0,0,0.20)]', className)}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
    >
      <div className="relative h-[142px] rounded-t-2xl bg-gray-400">
        {habitat.backgroundImage && (
          <img
            src={habitat.backgroundImage}
            alt={habitat.animal}
            className="preflight preflight-img size-full overflow-hidden rounded-t-2xl object-cover"
          />
        )}
        <HabitatStatus
          online={habitat.isStreamOn}
          liveTalk={habitat.liveTalk}
          shadow="small"
          className="absolute right-3 top-2"
        />
        <div className="absolute inset-x-0 -bottom-7 flex items-center justify-center">
          <div className="relative">
            {habitat.profileImage ? (
              <img
                src={habitat.profileImage}
                alt={habitat.animal}
                className="preflight preflight-img size-[108px] overflow-hidden rounded-full border border-white object-cover"
              />
            ) : (
              <div className="preflight flex size-[108px] items-center justify-center overflow-hidden rounded-full border border-white bg-gray-400 text-2xl font-bold text-white">
                {habitat.animal.charAt(0)}
              </div>
            )}
            {habitat.zoo && (
              <a href={`/h/${habitat.zoo.slug}`} className="preflight preflight-a absolute -right-3 bottom-0">
                <div className="rounded-full bg-white shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)]">
                  <img
                    src={habitat.zoo.logo}
                    alt={habitat.zoo.name}
                    className="preflight preflight-img size-10 overflow-hidden rounded-full object-contain"
                  />
                </div>
              </a>
            )}
          </div>
        </div>
      </div>
      <div className="grow px-4 pt-8 text-center desktop:pt-9">
        <h3 className="preflight preflight-h3 text-base/tight font-semibold text-black desktop:text-xl/tight">
          {habitat.animal}
        </h3>
        <p className="preflight preflight-h4 mt-1 text-xs font-semibold uppercase text-stone-500">
          {habitat.title} at {habitat.zoo.name}
        </p>
        <p className="preflight preflight-p mt-2 line-clamp-3 text-sm text-stone-600">{habitat.description}</p>
      </div>
      <div className="px-4 pb-6 pt-3">
        {renderLink ? (
          renderLink({
            habitatId: habitat._id,
            slug: habitat.slug,
            zooSlug: habitat.zoo.slug,
            className: buttonVariants({ variant: 'default', size: 'default', className: 'preflight preflight-a w-full' }),
            children: 'Visit Habitat',
          })
        ) : (
          <HabitatLink
            habitatId={habitat._id}
            slug={habitat.slug}
            zooSlug={habitat.zoo.slug}
            className={buttonVariants({ variant: 'default', size: 'default', className: 'w-full' })}
          >
            Visit Habitat
          </HabitatLink>
        )}
      </div>
    </div>
  );
};

export default HabitatGridItem;
